import "../../../common/styles/list-style.css";
import { RouteComponentProps } from "react-router-dom";
import {
  useTranslate,
  Filter,
  useAuthenticated,
  Datagrid,
  ReactAdminComponentProps,
  Link,
  NumberField,
  FunctionField,
  Loading,
  Record,
  Responsive,
  SimpleList,
} from "react-admin";
import { Void } from "../../../common/data-grid-fields/Void";
import { CustomResourceRouteParams } from "../../../../common/types";
import { Breadcrumbs, Typography, Box, Theme } from "@material-ui/core";
import resourceNames from "../../../../common/data/resourceNames";
import TimeField from "../../../common/data-grid-fields/TimeField";
import { useTheme } from "@material-ui/styles";
import IndeterminateCheckBoxIcon from "@material-ui/icons/IndeterminateCheckBox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import HelpIcon from "@material-ui/icons/Help";
import { ArrayField } from "../../../common/data-grid-fields/ArrayField";
import { useDriver } from "../../../../common/hooks/useDriver";
import ReportIcon from "@material-ui/icons/Report";
import { GenericDateOrTimePicker } from "../../../common/form/GenericDateOrTimePicker";
import { DatePicker } from "@material-ui/pickers";
import { DriverLogGridViewDto } from "../../../../common/data/service-proxies";
import { DefaultProLogsList } from "../../../common/list/DefaultProLogsList";
import { DriverNameBreadcrumb, SelectedDriver } from "./DriverNameBreadcrumb";
import ShowIcon from "@material-ui/icons/RemoveRedEye";
import ProLogsDateField from "../../../common/data-grid-fields/ProLogsDateField";

interface DriverLogsProps
  extends Partial<RouteComponentProps<CustomResourceRouteParams>> {}

interface BreadcrumbProps {
  driver: SelectedDriver;
}

const Breadcrumb = ({ driver }: BreadcrumbProps) => {
  const translate = useTranslate();
  return (
    <Breadcrumbs aria-label="breadcrumb">
      <Link to={`/${resourceNames.HOS_LOGS}`}>
        <Typography variant="h6">
          {translate("resources.hos-logs.name")}
        </Typography>
      </Link>
      <DriverNameBreadcrumb
        driver={driver}
        getBasePath={(x) => `/${resourceNames.HOS_DRIVER_LOGS}/${x.id}`}
      />
    </Breadcrumbs>
  );
};

export const DriverLogsList = (
  props: DriverLogsProps & ReactAdminComponentProps
) => {
  useAuthenticated();
  const translate = useTranslate();
  const theme: Theme = useTheme();
  const { driver, loading } = useDriver(props.match?.params.id);

  interface SimpleListProps {
    record?: Record;
  }

  const RenderPrimaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "14px",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" style={{ fontWeight: "600" }}>
            Date:
          </Typography>
          <ProLogsDateField
            source="id"
            label="resources.hos-driver-logs.labels.date"
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" style={{ fontWeight: "600" }}>
            Distance (mi):
          </Typography>
          <NumberField
            source="distanceInMiles"
            label="resources.hos-driver-logs.labels.distanceInMiles"
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" style={{ fontWeight: "600" }}>
            Time on duty:
          </Typography>
          <TimeField
            record={record}
            source="minutesWorked"
            inputType="minutes"
            outputType="short"
            label="resources.hos-driver-logs.labels.minutesWorked"
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" style={{ fontWeight: "600" }}>
            Violations:
          </Typography>
          <ArrayField
            source="violations"
            record={record}
            label="resources.hos-driver-logs.labels.violations"
            displayedElementsCount={1}
            emptyArrayDisplay={translate(
              "resources.hos-driver-logs.labels.noViolations"
            )}
            labelTranslationNamespace="appData.violationTypes"
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "4px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" style={{ fontWeight: "600" }}>
            Has inspection report:
          </Typography>
          <FunctionField<DriverLogGridViewDto & Record>
            source="hasInspectionReport"
            headerClassName="centered-content"
            label="resources.hos-driver-logs.labels.hasInspectionReport"
            render={(record) => (
              <Box className="centered-content">
                {record?.hasInspectionReport ? (
                  <CheckBoxIcon
                    style={{
                      color: theme.palette.success.main,
                    }}
                  />
                ) : (
                  <IndeterminateCheckBoxIcon
                    style={{
                      color: theme.palette.error.main,
                    }}
                  />
                )}
              </Box>
            )}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "4px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" style={{ fontWeight: "600" }}>
            Defects found:
          </Typography>
          <FunctionField<DriverLogGridViewDto & Record>
            source="defectsFound"
            headerClassName="centered-content"
            label="resources.hos-driver-logs.labels.foundDefects"
            render={(record) => (
              <Box className="centered-content">
                {record?.defectsFound === true ? (
                  <ReportIcon
                    style={{
                      color: theme.palette.error.main,
                    }}
                  />
                ) : record?.defectsFound === false ? (
                  <IndeterminateCheckBoxIcon
                    style={{
                      color: theme.palette.success.main,
                    }}
                  />
                ) : (
                  <HelpIcon
                    style={{
                      color: theme.palette.primary.main,
                    }}
                  />
                )}
              </Box>
            )}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "4px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" style={{ fontWeight: "600" }}>
            Log certified:
          </Typography>
          <FunctionField<DriverLogGridViewDto & Record>
            source="certified"
            headerClassName="centered-content"
            label="resources.hos-driver-logs.labels.signed"
            render={(record) => (
              <Box className="centered-content">
                {record?.certified ? (
                  <CheckBoxIcon
                    style={{
                      color: theme.palette.success.main,
                    }}
                  />
                ) : (
                  <IndeterminateCheckBoxIcon
                    style={{
                      color: theme.palette.error.main,
                    }}
                  />
                )}
              </Box>
            )}
          />
        </div>
      </div>
    );
  };

  const LogsListFilter = (props: any) => {
    return (
      <Filter {...props}>
        <GenericDateOrTimePicker
          PickerComponent={DatePicker}
          className="logs-list-view-filter"
          label="resources.hos-logs.labels.dateFrom"
          source="dateFrom"
          alwaysOn
        />
        <GenericDateOrTimePicker
          PickerComponent={DatePicker}
          className="logs-list-view-filter"
          label="resources.hos-logs.labels.dateTo"
          source="dateTo"
          alwaysOn
        />
      </Filter>
    );
  };

  if (loading) {
    return <Loading />;
  }

  const selectedDriver = driver
    ? { id: driver.id, fullName: driver.fullName, driverId: driver.driverId }
    : {};
  return (
    <div className="content-container">
      <DefaultProLogsList
        title={<Breadcrumb driver={selectedDriver} />}
        bulkActionButtons={false}
        actions={<Void />}
        canExport={false}
        filters={<LogsListFilter />}
        filter={{ driverId: props.match?.params.id }}
        sort={{ field: "id", order: "DESC" }}
        {...props}
      >
        <Responsive
          small={
            <SimpleList
              className="simple-list-wrapper"
              linkType={false}
              primaryText={(record) => (
                <RenderPrimaryTextForSimpleList record={record} />
              )}
              rightIcon={(record) => (
                <Link
                  to={`/${resourceNames.HOS_DRIVER_LOG_VIEW}/${
                    driver?.id ?? 12345
                  }/${record.id}`}
                >
                  <ShowIcon
                    style={{
                      color: theme.palette.primary.main,
                    }}
                  />
                </Link>
              )}
            />
          }
          medium={
            <Datagrid
              rowClick={(id, _basePath, _record) => {
                return `/${resourceNames.HOS_DRIVER_LOG_VIEW}/${
                  driver?.id ?? 12345
                }/${id}`;
              }}
            >
              <ProLogsDateField
                source="id"
                label="resources.hos-driver-logs.labels.date"
              />
              <NumberField
                source="distanceInMiles"
                label="resources.hos-driver-logs.labels.distanceInMiles"
                sortable={false}
              />
              <TimeField
                source="minutesWorked"
                inputType="minutes"
                outputType="short"
                label="resources.hos-driver-logs.labels.minutesWorked"
                sortable={false}
              />
              <FunctionField<DriverLogGridViewDto & Record>
                source="hasInspectionReport"
                headerClassName="centered-content"
                label="resources.hos-driver-logs.labels.hasInspectionReport"
                render={(record) => (
                  <Box className="centered-content">
                    {record?.hasInspectionReport ? (
                      <CheckBoxIcon
                        style={{
                          color: theme.palette.success.main,
                        }}
                      />
                    ) : (
                      <IndeterminateCheckBoxIcon
                        style={{
                          color: theme.palette.error.main,
                        }}
                      />
                    )}
                  </Box>
                )}
              />
              <FunctionField<DriverLogGridViewDto & Record>
                source="defectsFound"
                headerClassName="centered-content"
                label="resources.hos-driver-logs.labels.foundDefects"
                render={(record) => (
                  <Box className="centered-content">
                    {record?.defectsFound === true ? (
                      <ReportIcon
                        style={{
                          color: theme.palette.error.main,
                        }}
                      />
                    ) : record?.defectsFound === false ? (
                      <IndeterminateCheckBoxIcon
                        style={{
                          color: theme.palette.success.main,
                        }}
                      />
                    ) : (
                      <HelpIcon
                        style={{
                          color: theme.palette.primary.main,
                        }}
                      />
                    )}
                  </Box>
                )}
              />
              <FunctionField<DriverLogGridViewDto & Record>
                source="certified"
                headerClassName="centered-content"
                label="resources.hos-driver-logs.labels.signed"
                render={(record) => (
                  <Box className="centered-content">
                    {record?.certified ? (
                      <CheckBoxIcon
                        style={{
                          color: theme.palette.success.main,
                        }}
                      />
                    ) : (
                      <IndeterminateCheckBoxIcon
                        style={{
                          color: theme.palette.error.main,
                        }}
                      />
                    )}
                  </Box>
                )}
              />
              <ArrayField
                sortable={false}
                source="violations"
                label="resources.hos-driver-logs.labels.violations"
                displayedElementsCount={1}
                emptyArrayDisplay={translate(
                  "resources.hos-driver-logs.labels.noViolations"
                )}
                labelTranslationNamespace="appData.violationTypes"
              />
            </Datagrid>
          }
        />
      </DefaultProLogsList>
    </div>
  );
};
